/**
 * SellerHub — landing page for the Selling section.
 * Composes seller listing data and tour requests into a dashboard summary.
 */
import React, { useEffect, useState } from 'react';
import { PageHeader, SectionCard, RoleBadge } from '../ui';
import { useSellerTourRequests } from '../../agents/tourRequestAgent';
import { useOffersData } from '../OfferManagement/hooks/useOffersData';
import { getRoleTurn } from '../OfferManagement/utils';
import APIAgent from '../../agents/apiAgent';
import { useSellerJourneys } from '../../agents/journeyAgent';
import styles from './SellerHub.module.css';

const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const STATUS_LABELS = {
  draft: 'Draft',
  active: 'Active',
  pending: 'Pending',
  sold: 'Sold',
  withdrawn: 'Withdrawn',
};

export default function SellerHub({ user, onNavigate, sharedListings, setSharedListings }) {
  const sellerId = user?.id;
  const [listingsLoading, setListingsLoading] = useState(false);
  const [listingsError, setListingsError] = useState(null);
  const [localListings, setLocalListings] = useState([]);
  const {
    tours,
    loading: toursLoading,
    error: toursError,
    fetchSellerTours,
  } = useSellerTourRequests(sellerId);
  const {
    offers,
    loading: offersLoading,
    error: offersError,
  } = useOffersData({ userId: sellerId, role: 'seller' });
  // A deal recorded as ended keeps its offer "accepted"; its transaction says
  // it ended. Leave those out of "Active transactions" (QA 2026-10-16).
  const { journeys: sellerJourneys } = useSellerJourneys(sellerId);

  const listings = Array.isArray(sharedListings) ? sharedListings : localListings;
  const updateListings = typeof setSharedListings === 'function' ? setSharedListings : setLocalListings;

  useEffect(() => {
    if (!sellerId || listings.length > 0) return;
    let cancelled = false;
    setListingsLoading(true);
    setListingsError(null);
    APIAgent.getSellerListings(sellerId)
      .then((res) => {
        if (cancelled) return;
        updateListings(res?.houses || []);
      })
      .catch((err) => {
        if (!cancelled) {
          setListingsError(err?.message || 'Failed to load listings');
          updateListings([]);
        }
      })
      .finally(() => { if (!cancelled) setListingsLoading(false); });
    return () => { cancelled = true; };
  }, [sellerId, listings.length, updateListings]);

  // `useSellerTourRequests` is a lazy hook — it never self-loads. Without this
  // the tours list stayed permanently empty and the pending count read 0.
  useEffect(() => {
    fetchSellerTours();
  }, [fetchSellerTours]);

  const counts = listings.reduce((acc, l) => {
    const status = (l.listing_status || l.status || 'draft').toLowerCase();
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  // Requests still needing the seller: new ones and approved ones awaiting a
  // time (the same set as the Tour Calendar's Pending Requests).
  const pendingTours = (tours || []).filter(
    (t) => t.status === 'pending' || t.status === 'seller_approved'
  );

  // "Active transactions" = this seller's accepted offers.
  //
  // This used to be derived from listing_status ∈ {pending, sold}, which meant
  // the card was permanently empty: nothing in the product ever moved a house
  // off "active". Acceptance now promotes the listing to "pending", but the
  // offers are still the better source — they survive listings that were
  // accepted before that fix, and they carry the price and the counterparty,
  // which a listing row does not.
  const endedOfferIds = new Set(
    (sellerJourneys || [])
      .filter((j) => (j.transaction?.status || '').toLowerCase() === 'terminated')
      .map((j) => j.transaction?.offer_id)
      .filter((id) => id != null),
  );
  const acceptedOffers = (offers || []).filter(
    (o) => (o.status || '').toLowerCase() === 'accepted' && !endedOfferIds.has(o.id),
  );
  // Offers it's the seller's move on (a new offer, or a counter the buyer sent
  // back) — the most time-sensitive seller event, badged on the quick-action.
  const offersAwaitingSeller = (offers || []).filter(
    (o) => getRoleTurn(o, 'seller') === 'you',
  ).length;

  return (
    <div className="seller-hub">
      <PageHeader
        eyebrow={<RoleBadge role="seller" />}
        title="Your selling activity"
        subtitle="AI-powered market intelligence — Cut the 6%"
      />

      <div className="ui-quick-actions">
        <button
          className="ui-quick-actions__btn ui-quick-actions__btn--primary"
          onClick={() => onNavigate?.('seller-post')}
        >
          <span aria-hidden="true">➕</span> Post a listing
        </button>
        <button className="ui-quick-actions__btn" onClick={() => onNavigate?.('seller-listings')}>
          <span aria-hidden="true">🗂️</span> My listings
        </button>
        <button className="ui-quick-actions__btn" onClick={() => onNavigate?.('seller-tours')}>
          <span aria-hidden="true">🗓</span> Tour requests
        </button>
        <button className="ui-quick-actions__btn" onClick={() => onNavigate?.('offers:seller')}>
          <span aria-hidden="true">📨</span> Offers received
          {!offersLoading && offersAwaitingSeller > 0 && (
            <span
              className="ui-quick-actions__badge"
              data-testid="seller-offers-awaiting-badge"
              aria-label={`${offersAwaitingSeller} awaiting your response`}
            >
              {offersAwaitingSeller}
            </span>
          )}
        </button>
      </div>

      <div className="ui-hub-grid">
        <SectionCard
          title="My listings"
          action="Manage"
          onAction={() => onNavigate?.('seller-listings')}
        >
          {listingsLoading && <div className="ui-empty">Loading…</div>}
          {!listingsLoading && listingsError && (
            <div className="ui-empty">Failed to load listings — try refreshing.</div>
          )}
          {!listingsLoading && !listingsError && listings.length === 0 && (
            <div className="ui-empty">No listings yet — post your first home.</div>
          )}
          {!listingsLoading && !listingsError && listings.length > 0 && (
            <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
              {Object.entries(counts).map(([status, count]) => (
                <div
                  key={status}
                  style={{
                    background: 'var(--color-bg-inset)',
                    borderRadius: 'var(--radius-md)',
                    padding: '10px 14px',
                    minWidth: 90,
                  }}
                >
                  <div style={{ fontSize: 22, fontWeight: 700 }}>{count}</div>
                  <div className={styles['hub-list-item__meta']}>
                    {STATUS_LABELS[status] || status}
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        <SectionCard
          title="Pending tour requests"
          action="Review"
          onAction={() => onNavigate?.('seller-tours')}
        >
          {toursLoading && <div className="ui-empty">Loading…</div>}
          {!toursLoading && toursError && (
            <div className="ui-empty">Failed to load — try refreshing.</div>
          )}
          {!toursLoading && !toursError && (
            <div className={styles['hub-count']} data-testid="pending-tours-count">
              <span className={styles['hub-count__value']}>{pendingTours.length}</span>
              <span className={styles['hub-list-item__meta']}>
                {pendingTours.length === 1 ? 'Pending request' : 'Pending requests'}
              </span>
            </div>
          )}
          {!toursLoading && !toursError && pendingTours.length === 0 && (
            <div className="ui-empty">No pending requests.</div>
          )}
          {!toursLoading && !toursError && pendingTours.slice(0, 4).map((t) => (
            <div key={t.id} className={styles['hub-list-item']}>
              <div style={{ fontWeight: 600 }}>{t.property_address || `Property #${t.house_id}`}</div>
              <div className={styles['hub-list-item__meta']}>
                {t.preferred_date || 'Date TBD'} · from {t.buyer_name || 'buyer'}
              </div>
            </div>
          ))}
        </SectionCard>

        <SectionCard
          title="Active transactions"
          action="View journey"
          onAction={() => onNavigate?.('journey:seller')}
        >
          {offersLoading && <div className="ui-empty">Loading…</div>}
          {!offersLoading && offersError && (
            // Card-specific wording: three cards on this page can fail
            // independently, and an identical message in each leaves the seller
            // unable to tell which one is stale.
            <div className="ui-empty">Failed to load transactions — try refreshing.</div>
          )}
          {!offersLoading && !offersError && acceptedOffers.length === 0 && (
            <div className="ui-empty">
              No active transactions yet — accepted offers and closings show here.
            </div>
          )}
          {!offersLoading && !offersError && acceptedOffers.slice(0, 4).map((o) => (
            <div key={o.id} className={styles['hub-list-item']}>
              <div style={{ fontWeight: 600 }}>
                {o.property_address || `Listing #${o.property_id}`}
              </div>
              <div className={styles['hub-list-item__meta']}>
                Accepted
                {o.offer_price ? ` · ${currency.format(o.offer_price)}` : ''}
                {o.closing_date ? ` · closes ${o.closing_date}` : ''}
              </div>
            </div>
          ))}
        </SectionCard>
      </div>
    </div>
  );
}
